import axios from "axios";
import { API_BASE_URL } from "configs/AppConfig";
import { clearUserData, sessionSignOut } from "store/slices/authSlice";
import {
  startLoading,
  stopLoading,
  resetLoading,
} from "store/slices/loadingSlice";
import {
  setMaintenance,
  clearMaintenance,
} from "store/slices/maintenanceSlice";
import store from "../store";
import { AUTH_TOKEN } from "constants/AuthConstant";
import { Button, notification } from "antd";
import { SESSIONS_EXPIRED_TEXT } from "constants/AppConstants";
import {
  encryptAES,
  decryptAES,
  encryptParams,
  decryptParams,
} from "../../src/utils/aesDycrypt";
import { env } from "configs/EnvironmentConfig";

const unauthorizedCode = [400, 401, 403];
const { AES_KEY, NEED_ENCRYPT_DECRYPT, ENCRYPT_PARAMS, SKIP_ENCRYPTION_PATHS } =
  env;

// Create a cancel token source
let cancelTokenSource = axios.CancelToken.source();

const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  cancelToken: cancelTokenSource.token,
  transformResponse: [
    function (data) {
      // Don't transform HTML responses
      if (typeof data === "string" && data.includes("<html>")) {
        return data;
      }
      // Default JSON parsing for other responses
      try {
        return JSON.parse(data);
      } catch (e) {
        return data;
      }
    },
  ],
});

const displayHTMLContent = (htmlContent) => {
  // Directly replace the current page with the HTML content
  console.log("Replacing current page with HTML content");
  document.open();
  document.write(htmlContent);
  document.close();
};

// Helper function to check if path should skip parameter encryption
const shouldSkipEncryption = (url) => {
  return SKIP_ENCRYPTION_PATHS.some((path) => url.includes(path));
};

// Config
const TOKEN_PAYLOAD_KEY = "authorization";
const getEncryptState = () => {
  const state = store.getState();
  const encryption = state.auth?.encryption;
  return encryption;
};

// API Request interceptor
service.interceptors.request.use(
  async (config) => {
    // Dispatch loading start action
    store.dispatch(startLoading());

    console.warn(env, "evn");
    const encryption = getEncryptState() || false;
    console.warn(encryption, "encryption");
    // If there was a previous cancellation, create a new source
    if (cancelTokenSource.token.reason) {
      cancelTokenSource = axios.CancelToken.source();
    }

    config.cancelToken = cancelTokenSource.token;

    const jwtToken = localStorage.getItem(AUTH_TOKEN) || null;

    if (jwtToken) {
      config.headers[TOKEN_PAYLOAD_KEY] = `Bearer ${jwtToken}`;
    }
    if (
      encryption &&
      config.params &&
      Object.keys(config.params).length > 0 &&
      !shouldSkipEncryption(config.url)
    ) {
      try {
        console.log("[REQUEST] Original Params:", config.params);
        const encryptedParams = await encryptParams(config.params, AES_KEY);
        config.params = encryptedParams;
        console.log("[REQUEST] Params encrypted for transmission");
      } catch (error) {
        console.error("[REQUEST PARAMS ENCRYPTION ERROR]", error);
        // Continue with original params if encryption fails
      }
    }

    // Encrypt request body for POST/PUT requests
    if (
      encryption &&
      (config.method === "post" || config.method === "put") &&
      config.data &&
      !shouldSkipEncryption(config.url)
    ) {
      try {
        // Skip encryption for FormData (file uploads)
        if (config.data instanceof FormData) {
          console.log("[REQUEST] Skipping encryption for FormData");
        } else {
          // Only encrypt if content-type is application/json (or not specified, defaulting to JSON)
          const contentType =
            config.headers["Content-Type"] || config.headers["content-type"];
          if (!contentType || contentType.includes("application/json")) {
            console.log("[REQUEST] Original Data:", config.data);
            const encryptedData = await encryptAES(config.data, AES_KEY);
            config.data = { encrypted: encryptedData };
            console.log("[REQUEST] Data encrypted for transmission");
          }
        }
      } catch (error) {
        console.error("[REQUEST ENCRYPTION ERROR]", error);
        // Continue with original data if encryption fails
      }
    }

    return config;
  },
  (error) => {
    // Dispatch loading stop action on request error
    store.dispatch(stopLoading());

    // Do something with request error here
    notification.error({
      message: "Error",
    });
    Promise.reject(error);
  }
);

// API response interceptor
service.interceptors.response.use(
  async (response) => {
    // Dispatch loading stop action on response success
    store.dispatch(stopLoading());

    // A healthy (2xx) response on a NORMAL endpoint means the API is reachable
    // again, so auto-recover out of the maintenance screen if it was showing.
    // The maintenance control endpoints are exempt from the 503 (they return 200
    // even while maintenance is ON), so we ignore them here to avoid flicker.
    if (
      store.getState().maintenance?.active &&
      !response.config?.headers?.["x-maintenance-endpoint"]
    ) {
      store.dispatch(clearMaintenance());
    }

    console.log("response", response);
    const encryption = getEncryptState() || false;
    console.log(
      `%c[RESPONSE] URL: ${response.config.baseURL + response.config.url}`,
      "color: #2dce89; font-weight: bold;"
    );
    console.log("[RESPONSE] Raw Data:", response.data);
    const newToken = response.headers["new-token"];
    console.warn(newToken, "newtoken");

    if (response.config.headers?.["x-no-transform"]) {
      return response;
    }
    console.log("urlcheckinmg", shouldSkipEncryption(response.config.url));
    if (newToken && !shouldSkipEncryption(response.config.url)) {
      localStorage.setItem(AUTH_TOKEN, newToken);
    }
    // Decrypt response if it's encrypted
    if (encryption && response.data?.encrypted) {
      try {
        const decrypted = await decryptAES(response.data.encrypted, AES_KEY);
        console.log("[RESPONSE] Decrypted Data:", decrypted);
        return decrypted;
      } catch (e) {
        console.error("[DECRYPTION ERROR]", e);
        // Continue with original response if decryption fails
        console.warn("[DECRYPTION FALLBACK] Returning original response");
      }
    }
    // ( enc  for response ended)
    return response.data;
  },
  async (error) => {
    // Dispatch loading stop action on response error
    store.dispatch(stopLoading());

    // Check if the error is due to cancellation
    if (axios.isCancel(error)) {
      console.log("Request cancelled", error.message);
      return Promise.reject(error);
    }

    console.error(
      "%c[RESPONSE ERROR]",
      "color: #f5365c; font-weight: bold;",
      error
    );

    let notificationParam = { message: "" };

    if (error.response) {
      const { status, data, config } = error.response;

      // Maintenance mode: the server returns 503 with header `X-Maintenance: 1`
      // and a JSON body `{ maintenance: true, message }` on EVERY endpoint while
      // maintenance is ON. Detect that specific marker (header truthy OR
      // body.maintenance === true) and show the full-screen maintenance overlay.
      // Unrelated 503s (no marker) fall through to the existing handling below.
      const responseHeaders = error.response.headers || {};
      const maintenanceHeader = responseHeaders["x-maintenance"];
      const headerMarker =
        maintenanceHeader !== undefined &&
        maintenanceHeader !== null &&
        !["", "0", "false"].includes(
          String(maintenanceHeader).trim().toLowerCase()
        );
      const bodyMarker =
        data && typeof data === "object" && data.maintenance === true;

      if (status === 503 && (headerMarker || bodyMarker)) {
        const maintenanceMessage =
          data && typeof data === "object" && typeof data.message === "string"
            ? data.message
            : "";
        store.dispatch(setMaintenance(maintenanceMessage));
        // Resolve (rather than reject) to avoid noisy unhandled rejections while
        // the maintenance overlay is covering the screen.
        return Promise.resolve({ status: 503, maintenance: true });
      }

      // Handle 503 status - only display HTML content, no errors
      if (status === 503) {
        console.log("503 Service Unavailable - Raw response:", error.response);
        console.log("503 Data type:", typeof data);
        console.log("503 Data content:", data);
        console.log(
          "503 Data includes html?:",
          data && data.includes && data.includes("<html>")
        );

        if (data && typeof data === "string") {
          console.log("Displaying HTML content for 503 status");
          displayHTMLContent(data);
          // Return a resolved promise instead of rejecting to prevent error propagation
          return Promise.resolve({
            status: 503,
            data: data,
            displayedHTML: true,
          });
        }
        // If no HTML content, still don't throw error for 503
        return Promise.resolve({ status: 503, message: "Service Unavailable" });
      }

      if (status === 401 && data.status.status_code === SESSIONS_EXPIRED_TEXT) {
        const errorMessage = data.status.message;
        notificationParam.message = errorMessage;
        if (env !== "production") {
          notificationParam.description = data.status.status_code;
        }
        // Cancel all ongoing requests and reset loading state
        cancelTokenSource.cancel(
          "Session expired, canceling all pending requests"
        );
        store.dispatch(resetLoading());

        await store.dispatch(sessionSignOut());
        await store.dispatch(clearUserData());
      } else {
        // Handle other status codes (excluding 503)
        switch (status) {
          case 404:
            notificationParam.message = "Resource Not Found";
            notificationParam.description =
              "The requested resource could not be found. Please check the URL or try again later.";
            break;
          case 400:
            notificationParam.message = "Invalid Request";
            notificationParam.description =
              "The request could not be processed due to incorrect data. Please check your input and try again.";
            break;
          case 500:
            notificationParam.message = "Server Error";
            notificationParam.description =
              "An unexpected error occurred on the server. Please try again later.";
            break;
          case 508:
            notificationParam.message = "Timeout";
            notificationParam.description =
              "The server took too long to respond. Please check your internet connection or try again.";
            break;
          default:
            notificationParam.message = "Unexpected Error";
            notificationParam.description =
              "An unexpected error occurred. Please try again or contact support if the issue persists.";
        }
      }
    } else {
      notificationParam.message = "Network Error";
      notificationParam.description =
        "Unable to connect to the server. Please check your internet connection.";
      // Reset loading state on network error
      store.dispatch(resetLoading());
    }

    // Show notification for all errors except 503
    if (
      !error.config.url.includes("/logout") &&
      error.response?.status !== 503
    ) {
      notification.error(notificationParam);
    }

    return Promise.reject(error);
  }
);

export default service;
